import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Loader2, Briefcase, Calendar, DollarSign, UserCheck, Target, Plus, Save, Trash2, Receipt,
} from "lucide-react";
import InvoiceUploadModal from "./InvoiceUploadModal";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STATUSES = ["STARTING", "IN_PROGRESS", "WINDING_DOWN", "COMPLETED", "ON_HOLD"];

const STATUS_COLORS = {
  STARTING: "bg-sky-100 text-sky-800 border-sky-300",
  IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-300",
  WINDING_DOWN: "bg-amber-100 text-amber-800 border-amber-300",
  COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ON_HOLD: "bg-slate-100 text-slate-700 border-slate-300",
};

function fmtMoney(v, currency) {
  if (v == null || v === "") return "—";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP", maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency || ""} ${v}`;
  }
}

/**
 * EngagementDrawer — slide-over with all 4 sections of an engagement record:
 *   Scope & Commercials · Schedule & Milestones · Sponsor & Stakeholders · Outcomes
 */
export default function EngagementDrawer({ open, onClose, workspaceId, engagementId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [newSession, setNewSession] = useState({ date: "", session_type: "workshop" });
  const [newMilestone, setNewMilestone] = useState({ title: "", due_date: "", amount: "", currency: "GBP", kind: "invoice" });
  const [outcomes, setOutcomes] = useState([]);
  const [newOutcome, setNewOutcome] = useState({ nps_score: "", attendance_total: "", win_story_md: "", follow_on_md: "" });
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const baseUrl = `${API}/v2/workspaces/${workspaceId}/engagements/${engagementId}`;

  const load = useCallback(async () => {
    if (!workspaceId || !engagementId) return;
    setLoading(true);
    try {
      const [r, ro] = await Promise.all([
        fetch(baseUrl, { credentials: "include" }),
        fetch(`${baseUrl}/outcomes`, { credentials: "include" }),
      ]);
      if (!r.ok) throw new Error(`Failed (${r.status})`);
      const d = await r.json();
      setData(d);
      setOutcomes(ro.ok ? await ro.json() : []);

      // Phase 6: invoices for this engagement
      try {
        const ir = await fetch(`${API}/v2/workspaces/${workspaceId}/invoices?engagement_id=${engagementId}`, { credentials: "include" });
        if (ir.ok) setInvoices(await ir.json());
      } catch { /* ignore */ }
      setDraft({
        name: d.name || "",
        scope_md: d.scope_md || "",
        contracted_value: d.contracted_value ?? "",
        currency: d.currency || "GBP",
        region: d.region || "",
        start_date: d.start_date || "",
        end_date: d.end_date || "",
        lead_owner_email: d.lead_owner_email || "",
        sponsor_name: d.sponsor_name || "",
        sponsor_contact: d.sponsor_contact || "",
        notes_md: d.notes_md || "",
        status: d.status || "STARTING",
      });
    } catch (e) {
      toast.error(e.message || "Failed to load engagement");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, workspaceId, engagementId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const saveCore = async () => {
    setSaving(true);
    try {
      const body = { ...draft };
      Object.keys(body).forEach((k) => {
        if (body[k] === "") body[k] = null;
      });
      if (body.contracted_value !== null && body.contracted_value !== undefined) {
        body.contracted_value = parseFloat(body.contracted_value);
      }
      const r = await fetch(baseUrl, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      toast.success("Engagement updated");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addSession = async () => {
    if (!newSession.date) return toast.error("Date required");
    try {
      const r = await fetch(`${baseUrl}/sessions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newSession),
      });
      if (!r.ok) throw new Error("Failed to add session");
      toast.success("Session added");
      setNewSession({ date: "", session_type: "workshop" });
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const updateSessionStatus = async (sid, status) => {
    try {
      const r = await fetch(`${baseUrl}/sessions/${sid}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("Update failed");
      toast.success("Session updated");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const addMilestone = async () => {
    if (!newMilestone.title) return toast.error("Title required");
    try {
      const body = {
        ...newMilestone,
        amount: newMilestone.amount ? parseFloat(newMilestone.amount) : null,
        due_date: newMilestone.due_date || null,
      };
      const r = await fetch(`${baseUrl}/milestones`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Failed to add milestone");
      toast.success("Milestone added");
      setNewMilestone({ title: "", due_date: "", amount: "", currency: "GBP", kind: "invoice" });
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const updateMilestoneStatus = async (mid, status) => {
    try {
      const r = await fetch(`${baseUrl}/milestones/${mid}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("Update failed");
      toast.success("Milestone updated");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const softDelete = async () => {
    if (!confirm("Soft-delete this engagement?")) return;
    try {
      const r = await fetch(baseUrl, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Delete failed");
      toast.success("Engagement deleted");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveOutcome = async () => {
    setSavingOutcome(true);
    try {
      const body = {
        nps_score: newOutcome.nps_score === "" ? null : parseInt(newOutcome.nps_score),
        attendance_total: newOutcome.attendance_total === "" ? null : parseInt(newOutcome.attendance_total),
        win_story_md: newOutcome.win_story_md.trim() || null,
        follow_on_md: newOutcome.follow_on_md.trim() || null,
      };
      if (!body.win_story_md && !body.follow_on_md && body.nps_score == null && body.attendance_total == null) {
        toast.error("Capture at least one field");
        return;
      }
      const r = await fetch(`${baseUrl}/outcomes`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Failed to save outcome");
      toast.success("Outcome captured");
      setNewOutcome({ nps_score: "", attendance_total: "", win_story_md: "", follow_on_md: "" });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingOutcome(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="engagement-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-600" />
            {data?.name || "Engagement"}
            {data?.status && (
              <Badge variant="outline" className={`text-xs ${STATUS_COLORS[data.status] || ""}`}>
                {data.status.replace("_", " ")}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6 mt-4">
            {/* ─── 1. Scope & Commercials ─── */}
            <section data-testid="engagement-section-scope">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
                <DollarSign className="h-4 w-4 text-emerald-600" /> Scope &amp; Commercials
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600">Name</Label>
                  <Input data-testid="drawer-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Status</Label>
                  <select
                    data-testid="drawer-status"
                    value={draft.status || ""}
                    onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                    className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Lead owner email</Label>
                  <Input data-testid="drawer-lead-owner" value={draft.lead_owner_email || ""} onChange={(e) => setDraft({ ...draft, lead_owner_email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Contracted value</Label>
                  <Input data-testid="drawer-value" type="number" value={draft.contracted_value ?? ""} onChange={(e) => setDraft({ ...draft, contracted_value: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Currency</Label>
                  <select
                    value={draft.currency || "GBP"}
                    onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                    className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
                  >
                    {["GBP", "USD", "NGN", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Region</Label>
                  <select
                    value={draft.region || ""}
                    onChange={(e) => setDraft({ ...draft, region: e.target.value })}
                    className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
                  >
                    <option value="">—</option>
                    <option value="UK">UK</option>
                    <option value="NG">NG</option>
                    <option value="RoA">RoA</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Start date</Label>
                  <Input type="date" value={draft.start_date || ""} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">End date</Label>
                  <Input type="date" value={draft.end_date || ""} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600">Scope</Label>
                  <Textarea rows={3} value={draft.scope_md || ""} onChange={(e) => setDraft({ ...draft, scope_md: e.target.value })} />
                </div>
              </div>
            </section>

            {/* ─── 2. Schedule & Milestones ─── */}
            <section data-testid="engagement-section-schedule">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
                <Calendar className="h-4 w-4 text-blue-600" /> Schedule &amp; Milestones
              </h3>

              <div className="text-xs uppercase text-slate-500 mt-2 mb-1">Sessions</div>
              <ul className="space-y-1">
                {(data.sessions || []).map((s) => (
                  <li key={s.session_id} className="flex items-center gap-2 text-sm border border-slate-200 rounded p-2" data-testid={`session-row-${s.session_id}`}>
                    <span className="font-medium text-slate-700 w-28">{s.date}</span>
                    <span className="text-slate-500 w-24">{s.session_type || "—"}</span>
                    <Badge variant="outline" className="text-xs">{s.status}</Badge>
                    <span className="text-slate-500 ml-2">attendance: {s.attendance_count ?? "—"}</span>
                    <select
                      value={s.status}
                      onChange={(e) => updateSessionStatus(s.session_id, e.target.value)}
                      className="ml-auto h-7 border border-slate-300 rounded text-xs"
                    >
                      {["planned", "delivered", "cancelled"].map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </li>
                ))}
                {(data.sessions || []).length === 0 && <li className="text-xs text-slate-400">No sessions yet</li>}
              </ul>
              <div className="flex gap-2 mt-2">
                <Input type="date" value={newSession.date} onChange={(e) => setNewSession({ ...newSession, date: e.target.value })} className="w-40" data-testid="new-session-date" />
                <Input value={newSession.session_type} onChange={(e) => setNewSession({ ...newSession, session_type: e.target.value })} placeholder="type" className="w-32" data-testid="new-session-type" />
                <Button size="sm" onClick={addSession} data-testid="add-session-btn"><Plus className="h-3 w-3 mr-1" />Add</Button>
              </div>

              <div className="text-xs uppercase text-slate-500 mt-4 mb-1">Milestones</div>
              <ul className="space-y-1">
                {(data.milestones || []).map((m) => (
                  <li key={m.milestone_id} className="flex items-center gap-2 text-sm border border-slate-200 rounded p-2" data-testid={`milestone-row-${m.milestone_id}`}>
                    <span className="font-medium text-slate-700 flex-1">{m.title}</span>
                    <span className="text-slate-500 text-xs">{m.due_date || "—"}</span>
                    <span className="text-slate-500 text-xs">{fmtMoney(m.amount, m.currency)}</span>
                    <select
                      value={m.status}
                      onChange={(e) => updateMilestoneStatus(m.milestone_id, e.target.value)}
                      className="h-7 border border-slate-300 rounded text-xs"
                    >
                      {["pending", "invoiced", "paid", "cancelled"].map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </li>
                ))}
                {(data.milestones || []).length === 0 && <li className="text-xs text-slate-400">No milestones yet</li>}
              </ul>
              <div className="grid grid-cols-12 gap-2 mt-2">
                <Input value={newMilestone.title} onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })} placeholder="Title" className="col-span-5" data-testid="new-milestone-title" />
                <Input type="date" value={newMilestone.due_date} onChange={(e) => setNewMilestone({ ...newMilestone, due_date: e.target.value })} className="col-span-3" data-testid="new-milestone-date" />
                <Input type="number" value={newMilestone.amount} onChange={(e) => setNewMilestone({ ...newMilestone, amount: e.target.value })} placeholder="Amt" className="col-span-2" data-testid="new-milestone-amount" />
                <Button size="sm" onClick={addMilestone} className="col-span-2" data-testid="add-milestone-btn"><Plus className="h-3 w-3 mr-1" />Add</Button>
              </div>
            </section>

            {/* ─── 3. Sponsor & Stakeholders ─── */}
            <section data-testid="engagement-section-sponsor">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
                <UserCheck className="h-4 w-4 text-purple-600" /> Sponsor &amp; Stakeholders
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Sponsor name</Label>
                  <Input value={draft.sponsor_name || ""} onChange={(e) => setDraft({ ...draft, sponsor_name: e.target.value })} data-testid="drawer-sponsor-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Sponsor contact</Label>
                  <Input value={draft.sponsor_contact || ""} onChange={(e) => setDraft({ ...draft, sponsor_contact: e.target.value })} data-testid="drawer-sponsor-contact" />
                </div>
              </div>
            </section>

            {/* ─── 3.5 Invoices (Phase 6) ─── */}
            <section data-testid="engagement-section-invoices">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
                <Receipt className="h-4 w-4 text-emerald-600" /> Invoices
              </h3>
              {invoices.length === 0 ? (
                <p className="text-xs text-slate-400">No invoices yet — upload one and Rex will parse it.</p>
              ) : (
                <ul className="space-y-1">
                  {invoices.map((inv) => (
                    <li key={inv.invoice_id} className="flex items-center justify-between border border-slate-200 rounded p-2 text-sm" data-testid={`drawer-invoice-${inv.invoice_id}`}>
                      <div>
                        <p className="font-medium text-slate-800">{inv.number || inv.vendor || "Invoice"}</p>
                        <p className="text-xs text-slate-500">{inv.currency} {Number(inv.amount).toLocaleString()} · {inv.status} · due {inv.due_date || "—"}</p>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="outline" onClick={() => setInvoiceOpen(true)} data-testid="drawer-add-invoice-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add invoice
                </Button>
              </div>
            </section>

            {/* ─── 4. Outcomes / Notes ─── */}
            <section data-testid="engagement-section-outcomes">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
                <Target className="h-4 w-4 text-amber-600" /> Outcomes &amp; Notes
              </h3>
              <Textarea
                rows={3}
                value={draft.notes_md || ""}
                onChange={(e) => setDraft({ ...draft, notes_md: e.target.value })}
                placeholder="Working notes — used internally."
                data-testid="drawer-notes"
              />

              <div className="text-xs uppercase text-slate-500 mt-4 mb-1">Captured outcomes</div>
              {outcomes.length === 0 ? (
                <p className="text-xs text-slate-400">No outcomes captured yet.</p>
              ) : (
                <ul className="space-y-2">
                  {outcomes.map((o) => (
                    <li key={o.outcome_id} className="border border-slate-200 rounded p-2 text-sm" data-testid={`outcome-row-${o.outcome_id}`}>
                      <div className="flex items-baseline justify-between text-xs text-slate-500">
                        <span>{o.captured_at?.slice(0, 10)}</span>
                        <div className="flex gap-2">
                          {o.nps_score != null && <Badge variant="outline" className="text-xs">NPS {o.nps_score}</Badge>}
                          {o.attendance_total != null && <Badge variant="outline" className="text-xs">Attended {o.attendance_total}</Badge>}
                        </div>
                      </div>
                      {o.win_story_md && <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{o.win_story_md}</p>}
                      {o.follow_on_md && <p className="text-xs text-amber-700 mt-1">Follow-on: {o.follow_on_md}</p>}
                    </li>
                  ))}
                </ul>
              )}

              <div className="border border-slate-200 rounded p-3 mt-3 bg-slate-50/40 space-y-2">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Capture a new outcome</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-600">NPS (-100 to 100)</Label>
                    <Input data-testid="new-outcome-nps" type="number" value={newOutcome.nps_score} onChange={(e) => setNewOutcome({ ...newOutcome, nps_score: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Attendance total</Label>
                    <Input data-testid="new-outcome-attendance" type="number" value={newOutcome.attendance_total} onChange={(e) => setNewOutcome({ ...newOutcome, attendance_total: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Win story</Label>
                  <Textarea data-testid="new-outcome-win" rows={2} value={newOutcome.win_story_md} onChange={(e) => setNewOutcome({ ...newOutcome, win_story_md: e.target.value })} placeholder="What changed for the client / participants?" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Follow-on opportunities</Label>
                  <Textarea data-testid="new-outcome-followon" rows={2} value={newOutcome.follow_on_md} onChange={(e) => setNewOutcome({ ...newOutcome, follow_on_md: e.target.value })} placeholder="Cohort 2, expansion, referrals…" />
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={saveOutcome} disabled={savingOutcome} data-testid="capture-outcome-btn">
                    {savingOutcome && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    Capture outcome
                  </Button>
                </div>
              </div>
            </section>

            <div className="flex justify-between gap-2 sticky bottom-0 bg-white py-3 border-t border-slate-200">
              <Button variant="outline" size="sm" onClick={softDelete} data-testid="drawer-delete-btn" className="text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} data-testid="drawer-close-btn">Close</Button>
                <Button onClick={saveCore} disabled={saving} data-testid="drawer-save-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-1" /> Save changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
      <InvoiceUploadModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        workspaceId={workspaceId}
        engagementId={engagementId}
        onCreated={() => load()}
      />
    </Sheet>
  );
}
